import { Honeybadger } from '@honeybadger-io/react';

import { generateLedgerPdf } from 'api/Ledger';
import { generateRefundReceiptPdf } from 'api/RefundReceipt';
import {
  generateAppointmentReceiptsPdf,
  generateCheckinInvoicePdf,
  generateGoodFaithEstimatePdf,
  generateInvoicePdf,
  generatePdf,
  generatePreAuthPdf,
  generateQuotePdf,
  generateReceiptPdf,
  generateSuperbillPdf
} from 'api/Pdf';

import { seedQueuedExport } from 'components/practice/Export/ExportDockCard/lib/upsertExportJob';

import { streamPdf } from './lib/streamPdf';
import { deliverBlob } from './lib/blobDelivery';
import { openLoadingTab } from './lib/loadingTab';
import { registerPendingExportTab } from './lib/pendingExportTabs';

export { openLoadingTab };
export { downloadEligibilityBreakdownPdf } from './eligibilityBreakdown';

export const downloadPdf = async ({
  navigate,
  exportApiProps,
  title,
  view = false,
  viewTab: preOpenedTab
}) => {
  const viewTab = view ? preOpenedTab || openLoadingTab() : null;

  try {
    const result = await generatePdf(navigate, {
      title,
      ...exportApiProps,
      disposition: view ? 'inline' : 'attachment'
    });

    if (result && !(result instanceof Blob) && result.status === 'queued') {
      const tabIsWaiting = registerPendingExportTab(result.exportJobId, viewTab);
      if (!tabIsWaiting) viewTab?.close();

      seedQueuedExport(result.exportJobId, 'report');
      return true;
    }

    // Guard against a non-PDF 200 body (e.g. an HTML error/interstitial page):
    // requestApi returns the raw Blob when it can't parse the body as JSON, so
    // verify the content type before printing/downloading it as a PDF.
    if (!(result instanceof Blob) || result.type !== 'application/pdf') {
      viewTab?.close();
      return false;
    }
    deliverBlob(result, { title, viewTab });
    return true;
  } catch (error) {
    viewTab?.close();
    Honeybadger.notify(error, {
      name: '[error]downloadPdf',
      context: { title },
      tags: 'error,frontend,pdf'
    });
    return false;
  }
};

export const downloadReceiptPdf = ({
  navigate,
  transactionId,
  appointmentId,
  token,
  title = transactionId ? `Receipt #${transactionId}` : 'Receipt',
  view = false,
  print = false
}) =>
  streamPdf({
    request: (disposition) =>
      generateReceiptPdf(navigate, { transactionId, appointmentId, token, disposition }),
    title,
    view,
    print,
    context: { transactionId, appointmentId }
  });

export const downloadAppointmentReceiptsPdf = ({ navigate, appointmentId, print = false }) =>
  streamPdf({
    request: (disposition) =>
      generateAppointmentReceiptsPdf(navigate, { appointmentId, disposition }),
    title: 'Receipts',
    print,
    context: { appointmentId }
  });

export const downloadRefundReceiptPdf = ({
  navigate,
  transactionId,
  scheduleId,
  title = transactionId ? `Refund receipt RF-${transactionId}` : 'Refund receipt',
  view = false,
  print = false
}) =>
  streamPdf({
    request: (disposition) =>
      generateRefundReceiptPdf(navigate, { transactionId, scheduleId, disposition }),
    title,
    view,
    print,
    context: { transactionId, scheduleId }
  });

export const downloadLedgerPdf = ({
  navigate,
  ids,
  selectedColumns,
  title = 'Patient ledger',
  view = false,
  print = false
}) =>
  streamPdf({
    request: (disposition) => generateLedgerPdf(navigate, { ids, selectedColumns, disposition }),
    title,
    view,
    print,
    context: { ids }
  });

export const downloadInvoicePdf = ({
  navigate,
  ids,
  hiddenFields,
  memo,
  surcharges,
  aeob,
  title = 'Invoice',
  view = false,
  print = false,
  viewTab
}) =>
  streamPdf({
    request: (disposition) =>
      generateInvoicePdf(navigate, { ids, hiddenFields, memo, surcharges, aeob, disposition }),
    title,
    view,
    print,
    viewTab,
    context: { ids }
  });

export const downloadSuperbillPdf = ({
  navigate,
  superbillId,
  title = 'Superbill',
  view = false,
  print = false
}) =>
  streamPdf({
    request: (disposition) => generateSuperbillPdf(navigate, { superbillId, disposition }),
    title,
    view,
    print,
    context: { superbillId }
  });

export const downloadGoodFaithEstimatePdf = ({
  navigate,
  appointmentId,
  title = 'Good Faith Estimate',
  view = false,
  print = false,
  viewTab
}) =>
  streamPdf({
    request: (disposition) =>
      generateGoodFaithEstimatePdf(navigate, { appointmentId, disposition }),
    title,
    view,
    print,
    viewTab,
    context: { appointmentId }
  });

export const downloadCheckinInvoicePdf = ({
  navigate,
  appointmentId,
  title = 'Invoice',
  view = false,
  print = false,
  viewTab
}) =>
  streamPdf({
    request: (disposition) => generateCheckinInvoicePdf(navigate, { appointmentId, disposition }),
    title,
    view,
    print,
    viewTab,
    context: { appointmentId }
  });

export const downloadQuotePdf = ({
  navigate,
  id,
  title = 'Quote',
  view = false,
  print = false,
  viewTab
}) =>
  streamPdf({
    request: (disposition) => generateQuotePdf(navigate, { id, disposition }),
    title,
    view,
    print,
    viewTab,
    context: { id }
  });

export const downloadPreAuthPdf = ({
  navigate,
  appointmentId,
  cardId,
  title = 'Pre-authorized healthcare form',
  view = false,
  print = false,
  viewTab
}) =>
  streamPdf({
    request: (disposition) => generatePreAuthPdf(navigate, { appointmentId, cardId, disposition }),
    title,
    view,
    print,
    viewTab,
    context: { appointmentId, cardId }
  });
